import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { ArrowDown, Github, Linkedin, Mail} from 'lucide-react'
import { useState, useEffect } from 'react'

const Hero = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  })

  const [currentCourseIndex, setCurrentCourseIndex] = useState(0)
  const [hasScrolled, setHasScrolled] = useState(false)

  const courses = [
    "csci 1670: operating systems",
    "csci 1390: systems for machine learning",
    "math 1020: combinatorics",
    "csci 1690: operating systems lab"
  ]

  // Rotate through courses every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentCourseIndex((prev) => (prev + 1) % courses.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [courses.length])

  // Detect when scrolling begins
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setHasScrolled(true)
      } else if (window.scrollY === 0) {
        setHasScrolled(false)
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  }

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  }

  const socialLinks = [
    { icon: Linkedin, href: "https://linkedin.com/in/claireyluo", label: "LinkedIn" },
    { icon: Github, href: "https://github.com/claireyluo", label: "GitHub" },
    { icon: Mail, href: "mailto:claireluo.yx@gmail.com", label: "Email" }
  ]

  const currentlyItems = [
    { label: "interning ..", value: "software engineering intern @ databricks" },
    { label: "drinking ..", value: "🍑 & 🍓 green tea with boba" },
    { label: "interested in ..", value: "swe, quant, & product" }
  ]

//
//
//
// USE NPM RUN BUILD, NPM RUN DEPLOY
//
//
//


  return (
    <div className="relative min-h-screen flex items-start pt-20" ref={ref}>
      {/* Content */}
      <div className="container-max relative z-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]"
        >
          {/* Left Content */}
          <div className="space-y-8">
            <motion.div variants={itemVariants} className="space-y-4">
              <motion.p
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="text-primary font-mono text-xl"
              >
                Hi! I'm
              </motion.p>
              
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7 }}
                className="text-5xl md:text-7xl font-bold leading-tight group"
                style={{ marginBottom: '1.5rem' }}
              >
                <span className="text-primary group-hover:italic transition-all duration-300">Claire Luo ⋆˙⟡,</span>
              </motion.h1>
            </motion.div>

            <motion.p
              variants={itemVariants}
              className="text-xl text-muted font-mono max-w-lg"
            >
              a rising junior at brown university studying computer science && applied math
              <br />
              <br />
              driven by logic, creativity, & curiosity
            </motion.p>

            {/* Social Links & Ripple Hint - Animated Together */}
            <motion.div variants={itemVariants} className="flex flex-col gap-2">
              <div className="flex items-center space-x-4 mt-0.5">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group transition-transform hover:scale-110 rounded-xl"
                    aria-label={link.label}
                  >
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-200 border border-white/30 hover:border-white/50 shadow hover:shadow-xl backdrop-blur-none hover:bg-white/30">
                      <link.icon size={22} className="text-primary group-hover:text-primary-400 transition-colors" />
                    </span>
                  </a>
                ))}
              </div>
              {/* Ripple toggle hint - now directly underneath social icons */}
              <span className="text-muted animate-pulse duration-150 font-mono text-base mt-3">
                press <span style={{
                  display: 'inline-block',
                  padding: '2px 8px',
                  borderRadius: '6px',
                  background: 'rgba(200,200,200,0.25)',
                  border: '1.5px solid #bbb',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  fontSize: '1em',
                  margin: '0 2px',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
                }}>r</span> to toggle new mouse ripples
              </span>
            </motion.div>
          </div>

          {/* Right Content - Currently Section */}
          <motion.div
            variants={itemVariants}
            className="flex justify-center lg:justify-end items-start pt-16"
          >
            <motion.div 
              className="backdrop-blur-md bg-white/20 shadow-xl rounded-2xl p-8 max-w-md w-full"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <motion.h3 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-2xl font-bold text-primary mb-6"
              >
                currently    ⟢
              </motion.h3>
              
              <div className="space-y-4">
                {currentlyItems.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 1.0 + index * 0.1 }}
                    className="space-y-1"
                    whileHover={{ x: 5 }}
                  >
                    <p className="text-base text-muted font-mono italic tracking-wide">
                      {item.label}
                    </p>
                    <p className="text-primary font-medium">
                      {item.value}
                    </p>
                  </motion.div>
                ))}

                {/* Rotating Courses Section */}
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: 1.4 }}
                  className="space-y-2"
                  whileHover={{ x: 5 }}
                >
                  <p className="text-base text-muted font-mono italic tracking-wide">
                    learning [fall-26] ..
                  </p>
                  <div className="h-12 overflow-hidden">
                    <motion.p 
                      key={currentCourseIndex}
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      exit={{ y: -20, opacity: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                      className="text-primary font-medium leading-tight"
                    >
                      {courses[currentCourseIndex]}
                    </motion.p>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Animated Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: hasScrolled ? 0 : 1, y: hasScrolled ? -20 : 0 }}
        transition={{ duration: 0.5 }}
        className="absolute bottom-[90px] left-1/2 transform -translate-x-1/2 z-20"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="flex flex-col items-center space-y-1"
        >
          <span className="text-lg text-muted font-mono">scroll ..slowly:)</span>
          <ArrowDown size={24} className="text-muted" />
        </motion.div>
      </motion.div>
    </div>
  )
}

export default Hero 